import { FC, useMemo, useState } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { MonitoredUrl } from '../../types';
import { getUserPlanType, getMetricsForPlan } from '../../utils/monitoring';
import type { UserType } from '../../lib/auth/types';
import { CheckCircle2Icon, XCircleIcon, AlertTriangleIcon, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { Button, Tabs, TabsContent, TabsList, TabsTrigger, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Label, Checkbox } from '@pagespeedify/ui';

// Helper functions
const getMetricStatus = (metric: string, value: number | null): 'good' | 'needs improvement' | 'poor' | 'neutral' => {
  if (value === null) return 'neutral';
  const thresholds = {
    performance_score: { good: 90, poor: 50 },
    lcp: { good: 2500, poor: 4000 },
    cls: { good: 0.1, poor: 0.25 },
    fcp: { good: 1800, poor: 3000 },
    ttfb: { good: 800, poor: 1800 },
    tbt: { good: 200, poor: 600 },
    tti: { good: 3800, poor: 7300 },
  };
  const metricKey = metric.toLowerCase() as keyof typeof thresholds;
  const threshold = thresholds[metricKey];
  if (!threshold) return 'neutral';
  if (metric === 'performance_score') {
    if (value >= threshold.good) return 'good';
    if (value <= threshold.poor) return 'poor';
    return 'needs improvement';
  }
  if (value <= threshold.good) return 'good';
  if (value >= threshold.poor) return 'poor';
  return 'needs improvement';
};

const getRatingClasses = (rating: 'good' | 'needs improvement' | 'poor' | 'neutral') => {
  const classes = {
    good: { fill: '#22c55e', color: 'text-green-500' },
    'needs improvement': { fill: '#eab308', color: 'text-yellow-500' },
    poor: { fill: '#ef4444', color: 'text-red-500' },
    neutral: { fill: '#9ca3af', color: 'text-gray-500' },
  };
  return classes[rating];
};

const formatValue = (value: number, conversion = 1) => {
  const converted = value / conversion;
  return Number(converted.toFixed(2));
};

interface MonitoringChartsProps {
  history: any[];
  selectedUrl: MonitoredUrl | null;
  monitoringUsage?: any;
  user?: { uid: string; email: string | null; user_type?: UserType } | null;
}

export const MonitoringCharts: FC<MonitoringChartsProps> = ({ history, selectedUrl, monitoringUsage, user }) => {
  const [dateRange, setDateRange] = useState('last7days');
  const [chartType, setChartType] = useState<'average' | 'timeseries'>('average');
  const [showAllMetrics, setShowAllMetrics] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const [selectedExportMetrics, setSelectedExportMetrics] = useState<Record<string, boolean>>({
    scan_date: true,
    performance_score: true,
    lcp: true,
    cls: true,
    fcp: true,
    ttfb: true,
    tbt: true,
    tti: true,
  });

  const coreWebVitals = ['lcp', 'cls', 'tbt'];

  // Period options for button toggle
  const periodOptions = [
    { value: 'last7days', label: '7 days' },
    { value: 'last30days', label: '30 days' },
    { value: 'last90days', label: '3 months' },
    { value: 'thisYear', label: 'This year' },
    { value: 'allTime', label: 'All' },
  ];

  // Chart type options
  const chartTypeOptions = [
    { value: 'average', label: 'Average' },
    { value: 'timeseries', label: 'Trend' },
  ];

  // Smart date formatter based on period
  const formatDateForPeriod = (dateStr: string, period: string): string => {
    const date = new Date(dateStr);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    switch (period) {
      case 'last7days':
      case 'last30days':
        return `${date.getMonth() + 1}/${date.getDate()}`;
      case 'last90days':
        return `${monthNames[date.getMonth()]} ${date.getDate()}`;
      case 'thisYear':
        return monthNames[date.getMonth()];
      case 'allTime':
        return `${monthNames[date.getMonth()]} '${String(date.getFullYear()).slice(-2)}`;
      default:
        return `${date.getMonth() + 1}/${date.getDate()}`;
    }
  };

  const filteredHistory = useMemo(() => {
    const now = new Date();
    if (dateRange === 'last7days') {
      const cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return history.filter(h => new Date(h.scan_date) >= cutoff);
    }
    if (dateRange === 'last30days') {
      const cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return history.filter(h => new Date(h.scan_date) >= cutoff);
    }
    if (dateRange === 'last90days') {
      const cutoff = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      return history.filter(h => new Date(h.scan_date) >= cutoff);
    }
    if (dateRange === 'thisYear') {
      const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
      return history.filter(h => new Date(h.scan_date) >= firstDayOfYear);
    }
    return history;
  }, [history, dateRange]);

  // Aggregate to daily averages
  const aggregatedChartData = useMemo(() => {
    if (filteredHistory.length === 0) return [];

    const groupedByDate: Record<string, any[]> = {};
    filteredHistory.forEach(h => {
      const dateKey = new Date(h.scan_date).toISOString().split('T')[0];
      if (!groupedByDate[dateKey]) groupedByDate[dateKey] = [];
      groupedByDate[dateKey].push(h);
    });

    return Object.entries(groupedByDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateKey, scans]) => {
        const result: any = { date: formatDateForPeriod(dateKey, dateRange), rawDate: dateKey };
        const metricKeys = ['performance_score', 'lcp', 'cls', 'tti', 'fcp', 'ttfb', 'tbt'];
        metricKeys.forEach(key => {
          const values = scans
            .map(s => s[key])
            .filter(v => v !== null && v !== undefined)
            .map(v => Number(v))
            .filter(v => !Number.isNaN(v));
          if (values.length > 0) result[key] = values.reduce((a, b) => a + b, 0) / values.length;
        });
        return result;
      });
  }, [filteredHistory, dateRange]);

  // Calculate data span for "not enough data" check
  const dataSpanDays = useMemo(() => {
    if (!history || history.length < 2) return 0;
    const dates = history.map(h => new Date(h.scan_date).getTime());
    return Math.ceil((Math.max(...dates) - Math.min(...dates)) / (1000 * 60 * 60 * 24));
  }, [history]);

  const periodMinDays: Record<string, number> = {
    'last7days': 0, 'last30days': 8, 'last90days': 31, 'thisYear': 91, 'allTime': 0,
  };
  const hasEnoughData = dataSpanDays >= (periodMinDays[dateRange] || 0);

  if (!history || history.length < 1) {
    return <div className="text-center py-8 text-muted-foreground">Not enough data to display charts.</div>;
  }

  const userPlanType = getUserPlanType(monitoringUsage?.accessContext?.planFeatures, user?.user_type);
  const allowedMetricKeys = getMetricsForPlan(userPlanType);

  const allMetrics = [
    { key: 'lcp', name: 'LCP', unit: 's', conversion: 1000 },
    { key: 'cls', name: 'CLS', unit: '', conversion: 1 },
    { key: 'tbt', name: 'TBT', unit: 'ms', conversion: 1 },
    { key: 'performance_score', name: 'Score', unit: '', conversion: 1 },
    { key: 'fcp', name: 'FCP', unit: 's', conversion: 1000 },
    { key: 'ttfb', name: 'TTFB', unit: 's', conversion: 1000 },
    { key: 'tti', name: 'TTI', unit: 's', conversion: 1000 },
  ];

  const allAllowedMetrics = allMetrics.filter(m => m.key === 'performance_score' || allowedMetricKeys.includes(m.key));
  const metrics = showAllMetrics ? allAllowedMetrics : allAllowedMetrics.filter(m => coreWebVitals.includes(m.key));

  const metricThresholds = {
    performance_score: { ideal: 90, poor: 50 },
    lcp: { ideal: 2500, poor: 4000 },
    cls: { ideal: 0.1, poor: 0.25 },
    tti: { ideal: 3800, poor: 7300 },
    fcp: { ideal: 1800, poor: 3000 },
    ttfb: { ideal: 800, poor: 1800 },
    tbt: { ideal: 200, poor: 600 },
  };

  const average = (arr: number[]) => arr.length > 0 ? arr.reduce((p, c) => p + c, 0) / arr.length : 0;

  // Export metrics definition with units
  const exportableMetrics = [
    { key: 'scan_date', name: 'Scan Date', csvHeader: 'Scan Date' },
    { key: 'performance_score', name: 'Performance Score (0-100)', csvHeader: 'Performance Score' },
    { key: 'lcp', name: 'LCP (ms)', csvHeader: 'LCP(ms)' },
    { key: 'cls', name: 'CLS', csvHeader: 'CLS' },
    { key: 'fcp', name: 'FCP (ms)', csvHeader: 'FCP(ms)' },
    { key: 'ttfb', name: 'TTFB (ms)', csvHeader: 'TTFB(ms)' },
    { key: 'tbt', name: 'TBT (ms)', csvHeader: 'TBT(ms)' },
    { key: 'tti', name: 'TTI (ms)', csvHeader: 'TTI(ms)' },
  ];

  const toggleExportMetric = (key: string) => {
    setSelectedExportMetrics(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleExport = () => {
    const selectedKeys = Object.keys(selectedExportMetrics).filter(k => selectedExportMetrics[k]);
    if (selectedKeys.length === 0) return;

    // Get the metric definitions for selected keys
    const selectedMetricDefs = exportableMetrics.filter(m => selectedKeys.includes(m.key));

    const exportData = filteredHistory.map(h => {
      const row: Record<string, any> = {};
      selectedMetricDefs.forEach(metric => {
        // Use csvHeader as the key in exported data for clarity
        if (metric.key === 'scan_date') row[metric.csvHeader] = h.scan_date;
        else row[metric.csvHeader] = h[metric.key] ?? null;
      });
      return row;
    });

    const periodLabel = periodOptions.find(p => p.value === dateRange)?.label || dateRange;
    const urlSlug = selectedUrl?.website_url ? new URL(selectedUrl.website_url).hostname.replace(/\./g, '_') : 'monitoring';
    const filename = `${urlSlug}_${periodLabel.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`;

    if (exportFormat === 'csv') {
      // Use csvHeader for column headers (includes units like lcp_ms, ttfb_ms)
      const headers = selectedMetricDefs.map(m => m.csvHeader).join(',');
      const rows = exportData.map(row => selectedMetricDefs.map(m => row[m.csvHeader] ?? '').join(','));
      const csvContent = [headers, ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const jsonContent = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
    setShowExportModal(false);
  };

  // Render chart for a specific metric
  const renderChart = (metric: typeof metrics[0]) => {
    const metricValues = aggregatedChartData.map(h => h[metric.key]).filter(v => v !== null && v !== undefined);
    const overallAverage = average(metricValues);
    const thresholds = metricThresholds[metric.key as keyof typeof metricThresholds];
    const customThreshold = selectedUrl?.additional_metrics?.alerts?.[metric.key as keyof typeof metricThresholds]?.threshold;
    const rating = getMetricStatus(metric.key, overallAverage);
    const { fill } = getRatingClasses(rating);

    if (chartType === 'average') {
      const avgBarData = [{
        name: metric.name,
        current: formatValue(overallAverage, metric.conversion),
        threshold: formatValue((customThreshold || thresholds?.poor || 0), metric.conversion),
        ideal: formatValue(thresholds?.ideal || 0, metric.conversion),
      }];
      const barMax = Math.max(avgBarData[0].current, avgBarData[0].threshold, avgBarData[0].ideal);

      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">{metric.name} {metric.unit && `(${metric.unit})`}</h3>
            <span className={`text-2xl font-bold ${getRatingClasses(rating).color}`}>
              {formatValue(overallAverage, metric.conversion)}{metric.unit}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart layout="vertical" data={avgBarData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <XAxis type="number" domain={[0, barMax * 1.2]} tickFormatter={(v) => Number(v).toFixed(2)} />
              <YAxis type="category" dataKey="name" hide />
              <Tooltip contentStyle={{ backgroundColor: '#333', border: 'none' }} />
              <Bar dataKey="current" fill={fill} name="Current" radius={[0, 4, 4, 0]} />
              <Bar dataKey="ideal" fill="#3b82f6" name="Ideal" radius={[0, 4, 4, 0]} />
              <Bar dataKey="threshold" fill="#facc15" name="Poor" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: fill }}></span> Current</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500"></span> Ideal</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-500"></span> Poor Threshold</span>
          </div>
        </div>
      );
    } else {
      const minVal = Math.min(...metricValues);
      const maxVal = Math.max(...metricValues);
      const padding = (maxVal - minVal) * 0.2 || 1;

      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">{metric.name} {metric.unit && `(${metric.unit})`}</h3>
            <span className="text-sm text-muted-foreground">{aggregatedChartData.length} days</span>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={aggregatedChartData}>
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis domain={[minVal - padding, maxVal + padding]} tickFormatter={(v: number) => String(formatValue(v, metric.conversion))} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ backgroundColor: '#333', border: 'none' }} formatter={(v: number) => [formatValue(v, metric.conversion) + (metric.unit || ''), metric.name]} />
              <Legend />
              <Line type="monotone" dataKey={metric.key} stroke="#8884d8" name={metric.name} strokeWidth={2} dot={{ r: 3 }} />
              {thresholds && <ReferenceLine y={thresholds.ideal} stroke="green" strokeDasharray="5 5" label={{ value: 'Good', fill: 'green', fontSize: 10 }} />}
              {thresholds && <ReferenceLine y={thresholds.poor} stroke="orange" strokeDasharray="5 5" label={{ value: 'Poor', fill: 'orange', fontSize: 10 }} />}
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    }
  };

  return (
    <div className="space-y-4">
      {/* Top controls row */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Period Toggle */}
        <div className="flex flex-wrap gap-1 p-1 bg-muted/50 rounded-lg">
          {periodOptions.map(period => (
            <Button key={period.value} variant={dateRange === period.value ? 'default' : 'ghost'} size="sm"
              onClick={() => setDateRange(period.value)}
              className={`text-xs px-3 ${dateRange === period.value ? '' : 'text-muted-foreground hover:text-foreground'}`}>
              {period.label}
            </Button>
          ))}
        </div>

        {/* Right side controls */}
        <div className="flex items-center gap-2">
          {/* Chart Type Toggle */}
          <div className="flex gap-1 p-1 bg-muted/50 rounded-lg">
            {chartTypeOptions.map(opt => (
              <Button key={opt.value} variant={chartType === opt.value ? 'default' : 'ghost'} size="sm"
                onClick={() => setChartType(opt.value as 'average' | 'timeseries')}
                className={`text-xs px-3 ${chartType === opt.value ? '' : 'text-muted-foreground hover:text-foreground'}`}>
                {opt.label}
              </Button>
            ))}
          </div>

          {/* Export Button */}
          <Button variant="outline" size="sm" onClick={() => setShowExportModal(true)} className="gap-1.5">
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </div>
      </div>

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Export Monitoring Data</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-sm text-muted-foreground">
              Exporting data for: <span className="font-medium text-foreground">{periodOptions.find(p => p.value === dateRange)?.label}</span>
              <span className="text-xs ml-2">({filteredHistory.length} records)</span>
            </div>

            {/* Format Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Format</Label>
              <div className="flex gap-2">
                <Button variant={exportFormat === 'csv' ? 'default' : 'outline'} size="sm" onClick={() => setExportFormat('csv')}>CSV</Button>
                <Button variant={exportFormat === 'json' ? 'default' : 'outline'} size="sm" onClick={() => setExportFormat('json')}>JSON</Button>
              </div>
            </div>

            {/* Metric Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Metrics to include</Label>
              <div className="grid grid-cols-2 gap-2">
                {exportableMetrics.map(m => (
                  <div key={m.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`export-${m.key}`}
                      checked={selectedExportMetrics[m.key]}
                      onCheckedChange={() => toggleExportMetric(m.key)}
                    />
                    <label htmlFor={`export-${m.key}`} className="text-sm cursor-pointer">{m.name}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportModal(false)}>Cancel</Button>
            <Button onClick={handleExport} disabled={Object.values(selectedExportMetrics).every(v => !v)}>
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!hasEnoughData ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-muted/20 rounded-lg">
          <div className="text-4xl mb-4">📊</div>
          <h3 className="text-lg font-semibold mb-2">Not enough data</h3>
          <p className="text-muted-foreground text-sm max-w-md">
            You need at least {periodMinDays[dateRange]} days. You have {dataSpanDays} days.
          </p>
        </div>
      ) : (
        <Tabs defaultValue={metrics[0]?.key} className="flex flex-col md:flex-row gap-4">
          {/* Vertical Metric Tabs - compact */}
          <TabsList className="flex flex-row md:flex-col h-fit w-fit shrink-0 bg-muted/50 p-1 gap-1">
            {metrics.map(m => {
              const mValues = aggregatedChartData.map(h => h[m.key]).filter(v => v != null);
              const mRating = getMetricStatus(m.key, average(mValues));
              const Icon = mRating === 'good' ? CheckCircle2Icon : mRating === 'poor' ? XCircleIcon : AlertTriangleIcon;
              const { color } = getRatingClasses(mRating);
              return (
                <TabsTrigger key={m.key} value={m.key} className="flex-1 md:flex-none md:w-full justify-start gap-2 px-3 text-xs">
                  <Icon className={`h-3 w-3 ${color}`} />
                  {m.name}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Chart Content */}
          <div className="flex-1 min-w-0">
            {metrics.map(m => (
              <TabsContent key={m.key} value={m.key} className="bg-card p-4 rounded-lg">
                {renderChart(m)}
              </TabsContent>
            ))}
          </div>
        </Tabs>
      )}

      {/* Show More / Less toggle */}
      {allAllowedMetrics.length > coreWebVitals.length && (
        <div className="flex justify-center">
          <Button variant="ghost" size="sm" onClick={() => setShowAllMetrics(!showAllMetrics)} className="flex items-center gap-2 text-xs">
            {showAllMetrics ? <><ChevronUp className="h-4 w-4" /> Core Web Vitals Only</> : <><ChevronDown className="h-4 w-4" /> All Metrics ({allAllowedMetrics.length - coreWebVitals.length} more)</>}
          </Button>
        </div>
      )}
    </div>
  );
};
